import { styled } from 'styled-components';

import { LD, useLDFlag } from '@dutchie-pos/utils';

import { ImageIcon } from 'src/assets/image-icon';
import { BottomDrawer } from 'src/components/bottom-drawer';
import { Button } from 'src/components/button';
import { colors } from 'src/css/theme';
import { Path } from 'src/store/slices/user';
import { useAppSelector } from 'src/utils';

type ProfileDetails = {
  name?: string;
  phone?: string;
  dob: string;
  txnId?: string;
  address: string;
  addressUrl: string;
  idImage?: string | null;
  deliveryInstructions?: string;
};

type ViewProfileDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  profile: ProfileDetails;
};

export const ViewProfileDrawer = ({ isOpen, onClose, profile }: ViewProfileDrawerProps) => {
  const selectedPath = useAppSelector((state) => state.user.selectedPath);
  const useResponsiveV2 = useLDFlag(LD.IDENTIFICATION_PHOTO_PRESERVATION_V2, false);

  return (
    <BottomDrawer open={isOpen} onClose={onClose} marginX={0} fullHeight viewportFixed={useResponsiveV2}>
      <Container $useResponsiveV2={useResponsiveV2}>
        <TopSection>
          <Title>View profile</Title>

          <Button
            automationId='view-profile-drawer-close-button'
            ddActionName='Close (view profile drawer)'
            onClick={onClose}
            tertiary
          >
            Close
          </Button>
        </TopSection>
        <Separator />
        <MainContent data-testid='view-profile-drawer-content' $useResponsiveV2={useResponsiveV2}>
          <Name>{profile.name}</Name>
          {profile.idImage ? (
            <Image src={`data:image/png;base64, ${profile.idImage}`} alt={`${profile.name ?? ''} ID`} />
          ) : (
            <PlaceholderBox>
              <ImageIcon />
              <NoImageText>No image available</NoImageText>
            </PlaceholderBox>
          )}
          <Details>
            <DetailLine>
              {profile.phone && (
                <>
                  Phone #: <a href={`tel:${profile.phone}`}>{profile.phone}</a>{' '}
                </>
              )}
            </DetailLine>
            <DetailLine>DOB: {profile.dob}</DetailLine>
            <DetailLine>TxnID: {profile.txnId}</DetailLine>
            {selectedPath === Path.Delivery && (
              <DetailLine>
                <a href={profile.addressUrl}>{profile.address}</a>
              </DetailLine>
            )}
            {profile.deliveryInstructions && (
              <DetailLine>Delivery Instructions: {profile.deliveryInstructions}</DetailLine>
            )}
          </Details>
        </MainContent>
      </Container>
    </BottomDrawer>
  );
};

const Container = styled.div<{ $useResponsiveV2: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100%;
  ${({ $useResponsiveV2 }) => $useResponsiveV2 && 'min-height: 0;'}
`;

const TopSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.4rem 2.2rem 1.125rem;
  @media (max-width: 1280px) and (max-height: 720px), (max-width: 1195px) {
    padding: 1.5rem 2rem 1.125rem 1.5rem;
  }
`;

const Title = styled.h1`
  font-weight: bolder;
  font-size: 1.6rem;
  display: flex;
  gap: 1rem;
`;

const Separator = styled.hr`
  margin: 0;
  background-color: ${colors.dutchie.shadowGrey};
  height: 1px;
`;

const MainContent = styled.div<{ $useResponsiveV2: boolean }>`
  position: relative;
  display: flex;
  overflow: ${({ $useResponsiveV2 }) => ($useResponsiveV2 ? 'auto' : 'hidden')};
  min-height: ${({ $useResponsiveV2 }) => ($useResponsiveV2 ? '0' : 'auto')};
  padding: 20px;
  flex: ${({ $useResponsiveV2 }) => ($useResponsiveV2 ? '1' : 'initial')};
  flex-direction: column;
  gap: 20px;
  background-color: ${colors.gray10};
  height: ${({ $useResponsiveV2 }) => ($useResponsiveV2 ? 'auto' : '100%')};
`;

const Name = styled.div`
  font-weight: 700;
  font-size: 20px;
`;

const Image = styled.img`
  width: 100%;
  border-radius: 8px;
  object-fit: cover;
  display: block;
`;

const PlaceholderBox = styled.div`
  width: 100%;
  aspect-ratio: 1.5;
  border-radius: 8px;
  background-color: ${colors.gray20};
  border: 1px solid ${colors.gray30};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
`;

const NoImageText = styled.div`
  font-size: 18px;
  color: ${colors.gray40};
  font-weight: 600;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLine = styled.div`
  font-size: 16px;
  color: ${colors.accentGrey35};
`;
